import React, { useEffect, useState, useMemo } from "react";
import api from "../../services/api";
import { getFrontFileUrl } from "../../services/apiFront";
import {
  fetchRequirementsByAspirantWithStatus,
} from "../../services/requirementsService";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";

function getRowId(a) {
  const raw = a?.aspirant_id ?? a?.id ?? a?.user_id;
  return raw != null ? String(raw) : "undefined";
}

export default function ProofsAdmin() {
  const [aspirants, setAspirants] = useState([]);
  const [checkedAspirant, setCheckedAspirant] = useState(null);
  const [step, setStep] = useState(1); // 1: liste aspirants, 2: exigences
  const [currentParcours, setCurrentParcours] = useState(null);
  const [allRequirements, setAllRequirements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const [modal, setModal] = useState({
    open: false,
    title: "",
    message: "",
    variant: "success",
  });
  const openModal = (title, message, variant = "success") =>
    setModal({ open: true, title, message, variant });
  const closeModal = () => setModal((m) => ({ ...m, open: false }));

  useEffect(() => {
    loadAspirants();
  }, []);

  const loadAspirants = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get("/aspirants/admin/all");
      setAspirants(res.data?.aspirants || []);
    } catch (err) {
      console.error("Erreur chargement aspirants:", err);
      setError(err.response?.data?.error || "Erreur lors du chargement des aspirants");
    } finally {
        setLoading(false);
    }
  };

  // Filtrer les aspirants par parcours
  const chefGuide = useMemo(
    () => aspirants.filter((a) => a.parcours === "Chef Guide"),
    [aspirants]
  );
  const syl = useMemo(
    () => aspirants.filter((a) => a.parcours === "Senior Youth Leader"),
    [aspirants]
  );

  // Filtrer par recherche
  const filteredChefGuide = useMemo(() => {
    if (!searchTerm) return chefGuide;
    const term = searchTerm.toLowerCase();
    return chefGuide.filter(
      (a) =>
        a.name?.toLowerCase().includes(term) ||
        a.matricule?.toLowerCase().includes(term) ||
        a.eglise_name?.toLowerCase().includes(term) ||
        a.district_name?.toLowerCase().includes(term) ||
        a.region_name?.toLowerCase().includes(term)
    );
  }, [chefGuide, searchTerm]);

  const filteredSYL = useMemo(() => {
    if (!searchTerm) return syl;
    const term = searchTerm.toLowerCase();
    return syl.filter(
      (a) =>
        a.name?.toLowerCase().includes(term) ||
        a.matricule?.toLowerCase().includes(term) ||
        a.eglise_name?.toLowerCase().includes(term) ||
        a.district_name?.toLowerCase().includes(term) ||
        a.region_name?.toLowerCase().includes(term)
    );
  }, [syl, searchTerm]);

  /** Appelé quand on clique sur un aspirant */
  const handlePickAspirant = async (asp) => {
    if (!asp) return;

    const parcours = asp.parcours === "Chef Guide" ? "CG" : "SYL";

    setCheckedAspirant(asp);
    setCurrentParcours(parcours);
    setAllRequirements([]);

    try {
      const aspirantId = asp.aspirant_id ?? asp.id ?? asp.user_id;
      const data = await fetchRequirementsByAspirantWithStatus(aspirantId, {
        parcours,
      });

      const list = (Array.isArray(data) ? data : []).map((r) => ({
        ...r,
        _rid: r.requirement_id ?? r.id,
      }));

      setAllRequirements(list);
      setStep(2);
    } catch (e) {
      console.error("Erreur chargement requirements:", e?.response || e);
      openModal(
        "Erreur",
        "Impossible de charger les exigences de l'élève sélectionné.",
        "error"
      );
    }
  };

  const renderProgress = (aspirant) => {
    const progress = aspirant.progress || { done: 0, total: 0, percent: 0 };
    const { done, total, percent } = progress;
    const barClass =
      percent >= 90
        ? "bg-success"
        : percent >= 50
        ? "bg-info"
        : percent >= 25
        ? "bg-warning"
        : "bg-danger";

    return (
      <div className="d-flex align-items-center gap-2">
        <div className="progress rounded-pill" style={{ height: 18, flex: 1, minWidth: 100 }}>
          <div
            className={`progress-bar ${barClass}`}
            role="progressbar"
            style={{ width: `${Math.min(percent, 100)}%` }}
            aria-valuenow={percent}
            aria-valuemin="0"
            aria-valuemax="100"
          >
            <span className="small text-white fw-bold">{percent}%</span>
          </div>
        </div>
        <small className="text-muted d-none d-md-inline" style={{ whiteSpace: "nowrap" }}>
          {done}/{total}
        </small>
      </div>
    );
  };

  const renderAspirantTable = (title, aspirantsList, colorClass) => {
    const checkedMap = checkedAspirant ? { [getRowId(checkedAspirant)]: true } : {};
    // Abréviations pour mobile
    const shortTitle = title === "Chef Guide" ? "CG" : title === "Senior Youth Leader" ? "SYL" : title;

    return (
      <div className="card shadow-sm mb-4">
        <div className={`card-header ${colorClass} text-white`}>
          <h5 className="mb-0">
            <i className="bi bi-person-badge me-2"></i>
            <span className="d-inline d-md-none">{shortTitle}</span>
            <span className="d-none d-md-inline">{title}</span>
            <span className="badge bg-white text-dark ms-2">{aspirantsList.length}</span>
          </h5>
        </div>
        <div className="card-body p-0">
          {aspirantsList.length === 0 ? (
            <div className="alert alert-info m-3 mb-0">
              Aucun aspirant {title} trouvé.
            </div>
          ) : (
            <>
              {/* Version desktop : tableau */}
              <div className="table-responsive d-none d-md-block">
                <table className="table table-hover mb-0 rounded">
                  <thead className="table-light">
                    <tr>
                      <th>Nom</th>
                      <th>Matricule</th>
                      <th>Église</th>
                      <th>District</th>
                      <th>Région</th>
                      <th>Progression</th>
                    </tr>
                  </thead>
                  <tbody>
                    {aspirantsList.map((a) => {
                      const rowId = getRowId(a);
                      const isChecked = checkedMap[rowId] || false;
                      return (
                        <tr
                          key={rowId}
                          onClick={() => handlePickAspirant(a)}
                          style={{ cursor: "pointer" }}
                          className={isChecked ? "table-active" : ""}
                        >
                          <td className="fw-bold">{a.name}</td>
                          <td>{a.matricule || "—"}</td>
                          <td>{a.eglise_name || "—"}</td>
                          <td>{a.district_name || "—"}</td>
                          <td>{a.region_name || "—"}</td>
                          <td>{renderProgress(a)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Version mobile : cartes */}
              <div className="d-md-none p-2">
                {aspirantsList.map((a) => {
                  const rowId = getRowId(a);
                  const isChecked = checkedMap[rowId] || false;
                  const progress = a.progress || { done: 0, total: 0, percent: 0 };
                  const { done, total, percent } = progress;
                  const barClass =
                    percent >= 90
                      ? "bg-success"
                      : percent >= 50
                      ? "bg-info"
                      : percent >= 25
                      ? "bg-warning"
                      : "bg-danger";

                  return (
                    <div
                      key={rowId}
                      className={`card mb-3 shadow-sm ${isChecked ? "border-primary border-2" : ""}`}
                      onClick={() => handlePickAspirant(a)}
                      style={{ cursor: "pointer" }}
                    >
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <div className="flex-grow-1">
                            <h6 className="fw-bold mb-1">{a.name}</h6>
                            <div className="mb-2">
                              <small className="text-muted d-block">Matricule:</small>
                              <span>{a.matricule || "—"}</span>
                            </div>
                          </div>
                        </div>
                        
                        <div className="mb-2">
                          <small className="text-muted d-block">Église:</small>
                          <span>{a.eglise_name || "—"}</span>
                        </div>
                        
                        <div className="mb-2">
                          <small className="text-muted d-block">District:</small>
                          <span>{a.district_name || "—"}</span>
                        </div>

                        <div className="mb-2">
                          <small className="text-muted d-block">Région:</small>
                          <span>{a.region_name || "—"}</span>
                        </div>
                        
                        <div className="mb-2">
                          <small className="text-muted d-block mb-1">Progression:</small>
                          <div className="d-flex align-items-center gap-2">
                            <div className="progress rounded-pill" style={{ height: 18, flex: 1, minWidth: 100 }}>
                              <div
                                className={`progress-bar ${barClass}`}
                                role="progressbar"
                                style={{ width: `${Math.min(percent, 100)}%` }}
                                aria-valuenow={percent}
                                aria-valuemin="0"
                                aria-valuemax="100"
                              >
                                <span className="small text-white fw-bold">{percent}%</span>
                              </div>
                            </div>
                            <small className="text-muted" style={{ whiteSpace: "nowrap" }}>
                              {done}/{total}
                            </small>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  const renderRequirementsTable = (title, requirements, variant, allowResubmit = false) => {
    if (!requirements || requirements.length === 0) {
      return null;
    }

    const variantClass = {
      success: "table-success",
      warning: "table-warning",
      danger: "table-danger",
    }[variant] || "";

  return (
      <div className="card shadow-sm mb-3">
        <div className={`card-header bg-${variant} text-white`}>
          <h6 className="mb-0">{title}</h6>
      </div>
        <div className="card-body p-0">
          {/* Version desktop : tableau */}
          <div className="table-responsive d-none d-md-block">
            <table className={`table table-hover mb-0 ${variantClass} rounded`}>
              <thead className="table-light">
                <tr>
                  <th>Exigence</th>
                  <th>Fichier</th>
                  <th>Statut</th>
                  <th>Commentaire</th>
                </tr>
              </thead>
              <tbody>
                {requirements.map((r) => {
                  const fileUrl = r.justification_file
                    ? getFrontFileUrl(r.justification_file)
                    : null;
                  const statusDisplay = (r.status_display || "").toLowerCase();

                  return (
                    <tr key={r._rid || r.id}>
                      <td className="fw-bold">{r.title || r.requirement_title || "—"}</td>
                      <td>
                        {fileUrl ? (
                          <a
                            href={fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-sm btn-outline-primary"
                          >
                            <i className="bi bi-box-arrow-up-right me-1"></i>
                            Voir
                          </a>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td>
                        {statusDisplay === "valide" || r.is_validated === 1 ? (
                          <span className="badge bg-success">Validée</span>
                        ) : statusDisplay === "refuse" || r.is_validated === 0 ? (
                          <span className="badge bg-danger">Refusée</span>
                        ) : (
                          <span className="badge bg-warning text-dark">En attente</span>
                        )}
                      </td>
                      <td>
                        {r.comment ? (
                          <small className="text-muted">{r.comment}</small>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Version mobile : cartes */}
          <div className="d-md-none p-2">
            {requirements.map((r) => {
              const fileUrl = r.justification_file
                ? getFrontFileUrl(r.justification_file)
                : null;
              const statusDisplay = (r.status_display || "").toLowerCase();
              const isValidated = statusDisplay === "valide" || r.is_validated === 1;
              const isRefused = statusDisplay === "refuse" || r.is_validated === 0;
              const borderClass = isValidated ? "border-success" : isRefused ? "border-danger" : "border-warning";

              return (
                <div key={r._rid || r.id} className={`card mb-2 shadow-sm ${borderClass}`} style={{ borderWidth: 2 }}>
                  <div className="card-body p-3">
                    <div className="mb-2">
                      <strong className="d-block mb-1">Exigence:</strong>
                      <div style={{ wordBreak: "break-word" }}>
                        {r.title || r.requirement_title || "—"}
                      </div>
                    </div>
                    
                    <div className="mb-2">
                      <strong className="d-block mb-1">Fichier:</strong>
                      {fileUrl ? (
                        <a
                          href={fileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-sm btn-primary"
                        >
                          <i className="bi bi-box-arrow-up-right me-1"></i>
                          Voir le document
                        </a>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </div>
                    
                    <div className="mb-2">
                      <strong className="d-block mb-1">Statut:</strong>
                      {isValidated ? (
                        <span className="badge bg-success">Validée</span>
                      ) : isRefused ? (
                        <span className="badge bg-danger">Refusée</span>
                      ) : (
                        <span className="badge bg-warning text-dark">En attente</span>
                      )}
                    </div>
                    
                    {r.comment && (
                      <div className="mb-2">
                        <strong className="d-block mb-1">Commentaire:</strong>
                        <div className="text-break small text-muted">{r.comment}</div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  // Step 2: Afficher les exigences validées, en attente et refusées
  const renderStep2 = () => {
    if (!checkedAspirant) return null;

    const parcoursLabel = currentParcours === "CG" ? "Chef Guide" : "Senior Youth Leader";
    const parcoursLabelShort = currentParcours === "CG" ? "CG" : "SYL";

    // Séparer les exigences en 3 catégories
    const validatedReqs = allRequirements.filter((r) => {
      if (!r.justification_file) return false;
      const statusDisplay = (r.status_display || "").toLowerCase();
      return r.is_validated === 1 || statusDisplay === "valide";
    });

    const refusedReqs = allRequirements.filter((r) => {
      if (!r.justification_file) return false;
      const statusDisplay = (r.status_display || "").toLowerCase();
      const isRefusedStatus =
        r.is_validated === 0 ||
        statusDisplay === "refuse" ||
        statusDisplay === "rejete" ||
        statusDisplay === "rejeté";
      const hasComment = r.comment && r.comment.trim().length > 0;
      return isRefusedStatus && hasComment;
    });

    const pendingReqs = allRequirements.filter((r) => {
      if (!r.justification_file) return false;
      const statusDisplay = (r.status_display || "").toLowerCase();
      const isValidated = r.is_validated === 1 || statusDisplay === "valide";
      const isRefusedStatus =
        r.is_validated === 0 ||
        statusDisplay === "refuse" ||
        statusDisplay === "rejete" ||
        statusDisplay === "rejeté";
      const hasComment = r.comment && r.comment.trim().length > 0;
      const isRefused = isRefusedStatus && hasComment;
      return !isValidated && !isRefused;
    });

    return (
      <>
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-3 mb-md-4 gap-2">
          <h4 className="mb-0">
            Exigences pour {checkedAspirant.name} -{" "}
            <span className="text-muted d-inline d-md-none">{parcoursLabelShort}</span>
            <span className="text-muted d-none d-md-inline">{parcoursLabel}</span>
          </h4>
          <button 
            className="btn btn-secondary btn-sm"
            onClick={() => {
              setCheckedAspirant(null);
              setCurrentParcours(null);
              setAllRequirements([]);
              setStep(1);
            }}
          >
            <i className="bi bi-arrow-left me-1"></i>
            Retour à la liste
          </button>
        </div>

        {/* Exigences validées */}
        {renderRequirementsTable(
          `Exigences validées (${validatedReqs.length})`,
          validatedReqs,
          "success"
        )}

        {/* Exigences en attente */}
        {renderRequirementsTable(
          `Exigences en attente de validation (${pendingReqs.length})`,
          pendingReqs,
          "warning"
        )}

        {/* Exigences refusées */}
        {renderRequirementsTable(
          `Exigences refusées (${refusedReqs.length})`,
          refusedReqs,
          "danger"
        )}
      </>
    );
  };

  // Step 1: Afficher la liste des aspirants
  const renderStep1 = () => {
    return (
        <>
        <div className="d-flex align-items-center justify-content-between mb-4">
          <h4 className="fw-bold mb-0">Liste des aspirants</h4>
          <span className="badge bg-info text-dark">
            Total : {aspirants.length}
          </span>
        </div>

          <div className="row mb-4">
            <div className="col-md-6">
              <div className="input-group">
              <span className="input-group-text">
                <i className="bi bi-search"></i>
              </span>
                <input
                  type="text"
                  className="form-control"
                placeholder="Rechercher par nom, matricule, église, district, région..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
          </div>

        {error && (
          <div className="alert alert-danger shadow-sm mb-4">
            <i className="bi bi-exclamation-triangle me-2"></i>
            <strong>Erreur :</strong> {error}
                      <button
              className="btn btn-sm btn-outline-danger ms-3"
              onClick={loadAspirants}
            >
              Réessayer
                          </button>
                </div>
              )}

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Chargement...</span>
            </div>
            <p className="mt-3 text-muted">Chargement des aspirants...</p>
                </div>
              ) : (
          <>
            {renderAspirantTable("Chef Guide", filteredChefGuide, "bg-primary")}
            {renderAspirantTable("Senior Youth Leader", filteredSYL, "bg-danger")}
        </>
          )}
        </>
    );
  };

  return (
    <div className="container-fluid py-4">
      {step === 1 ? renderStep1() : renderStep2()}

      {/* Modal pour les messages */}
      {modal.open && (
        <div
          className="modal fade show"
          style={{ display: "block", background: "rgba(0,0,0,0.5)" }}
          onClick={closeModal}
        >
          <div
            className="modal-dialog"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content">
              <div className={`modal-header bg-${modal.variant} text-white`}>
                <h5 className="modal-title">{modal.title}</h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={closeModal}
                ></button>
              </div>
              <div className="modal-body">{modal.message}</div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={closeModal}>
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
